import {useCallback, useEffect, useRef} from 'react';

import {useDeliveryEstimate, QueryMetadata} from 'utilities/delivery-promise';
import {getConfiguration} from 'utilities/configuration';
import {usePromisePresentment} from 'utilities/promise-presenter';
import {ReportType, useErrorLogger} from 'utilities/error-logger';
import {isMobileDevice} from 'utilities/dom';
import {useMountStrategyName} from 'utilities/mount';

import {ShopPromisePerformance} from '../metrics';
import {BaseMetadata, MetadataMap} from '../types';

export function usePDPPerformanceMetrics(isModuleFullyLoaded: boolean) {
  const {deliveryEstimate} = useDeliveryEstimate();
  const presentment = usePromisePresentment(deliveryEstimate);
  const {reportError} = useErrorLogger();
  const mountStrategyName = useMountStrategyName();
  const metricsSentRef = useRef(false);

  const buildBaseMetadata = useCallback(() => {
    const metadata = new Map<BaseMetadata, string>();
    metadata.set(
      'has_faster_options',
      Boolean(presentment.fasterOptionsAvailableExtraLineMessage).toString(),
    );

    const {uxVersion} = getConfiguration();
    metadata.set('ux_version', uxVersion);

    if (presentment.hidePromise) {
      metadata.set('promise_brand', 'none');
    } else {
      metadata.set(
        'promise_brand',
        presentment.brandHandle?.toString() ?? 'unbranded',
      );
    }

    const {datacenter, shardId} = deliveryEstimate as QueryMetadata;
    if (datacenter || shardId) {
      metadata.set('response_server', `${datacenter}:${shardId}`);
    }

    if (navigator.connection?.effectiveType) {
      metadata.set('network_condition', navigator.connection.effectiveType);
    }

    const isMobile = isMobileDevice();
    metadata.set('is_mobile', isMobile.toString());

    const jsBundleInfo =
      ShopPromisePerformance.getAssetInfo('shopPromiseAsset');
    metadata.set('asset_cached', jsBundleInfo?.cached.toString() ?? 'false');

    if (mountStrategyName) {
      metadata.set('mount_strategy', mountStrategyName);
    }

    return Object.fromEntries(metadata) as MetadataMap;
  }, [deliveryEstimate, presentment, mountStrategyName]);

  useEffect(() => {
    if (deliveryEstimate && !metricsSentRef.current) {
      ShopPromisePerformance.measure('fcpToRender');
      ShopPromisePerformance.measure('realWorldTime', {
        startTimeMarkName: 'startParsingDOM',
      });
    }
  }, [deliveryEstimate]);

  useEffect(() => {
    if (deliveryEstimate && isModuleFullyLoaded && !metricsSentRef.current) {
      ShopPromisePerformance.measure('moduleFullyLoaded');
      const baseMetadata = buildBaseMetadata();
      const sendMetrics = async () => {
        try {
          metricsSentRef.current = true;
          await ShopPromisePerformance.sendMetrics(baseMetadata);
        } catch (error) {
          reportError({
            severity: ReportType.Metric,
            error,
            message: `Failed to report telemetry`,
          });
        }
      };
      sendMetrics();
    }
  }, [isModuleFullyLoaded, buildBaseMetadata, deliveryEstimate, reportError]);
}
